// Single Press Release View
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '../utils/api';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, ArrowLeft, Share2, Mail } from 'lucide-react';

export function PressRelease() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pr, setPR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPressRelease();
  }, [id]);

  async function loadPressRelease() {
    try {
      const response = await api.request(`/seo/press-releases/public/${id}`);
      setPR(response.press_release);
    } catch (err) {
      console.error('Failed to load press release:', err);
      setError('Press release not found');
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }

  function handleShare() {
    if (navigator.share) {
      navigator.share({
        title: pr.title,
        text: pr.summary,
        url: window.location.href
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !pr) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center py-12">
            <p className="text-gray-600 mb-4">{error || 'Press release not found'}</p>
            <Button onClick={() => navigate('/press')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Press Releases
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pr.title} | UPost Press Release</title>
        <meta name="description" content={pr.summary} />
        <meta property="og:title" content={pr.title} />
        <meta property="og:description" content={pr.summary} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={pr.distributed_at || pr.created_at} />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": pr.title,
            "description": pr.summary,
            "datePublished": pr.distributed_at || pr.created_at,
            "author": {
              "@type": "Organization",
              "name": "UPost"
            },
            "publisher": {
              "@type": "Organization",
              "name": "UPost",
              "logo": {
                "@type": "ImageObject",
                "url": "https://upost.com/logo.png"
              }
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-2">
                <Logo className="w-10 h-10" />
                <span className="text-xl font-bold">UPost</span>
              </Link>
              <nav className="hidden md:flex items-center space-x-6">
                <Link to="/blog" className="text-gray-600 hover:text-gray-900">Blog</Link>
                <Link to="/press" className="text-purple-600 font-medium">Press</Link>
                <Link to="/pricing" className="text-gray-600 hover:text-gray-900">Pricing</Link>
                <Link to="/login" className="text-gray-600 hover:text-gray-900">Log In</Link>
                <Link to="/register">
                  <Button size="sm">Sign Up</Button>
                </Link>
              </nav>
            </div>
          </div>
        </header>

        {/* Back Button */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/press')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Press Releases
          </Button>
        </div>

        {/* Press Release Content */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                Press Release
              </Badge>
              <Badge variant="outline">
                {pr.category || 'Technology'}
              </Badge>
              <div className="flex items-center text-sm text-gray-500">
                <Calendar className="w-4 h-4 mr-1" />
                {formatDate(pr.distributed_at || pr.created_at)}
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {pr.title}
            </h1>
            
            {pr.subtitle && (
              <p className="text-xl text-gray-600 mb-6">
                {pr.subtitle}
              </p>
            )}

            <div className="flex items-center gap-4 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <a 
                href={`mailto:?subject=${encodeURIComponent(pr.title)}&body=${encodeURIComponent(window.location.href)}`}
                className="inline-flex"
              >
                <Button variant="outline" size="sm">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </a>
            </div>
          </header>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div 
              className="text-gray-700 leading-relaxed whitespace-pre-wrap"
              style={{ fontSize: '1.125rem', lineHeight: '1.75' }}
            >
              {pr.content}
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg border">
            <h3 className="text-lg font-semibold mb-2">Media Contact</h3>
            <p className="text-gray-700">
              For media inquiries, please contact:<br />
              <a href={`mailto:${pr.contact_email}`} className="text-purple-600 hover:underline">
                {pr.contact_email}
              </a>
            </p>
          </div>

          {/* About UPost */}
          <div className="mt-8 p-6 bg-purple-50 rounded-lg border border-purple-100">
            <h3 className="text-lg font-semibold mb-2">About UPost</h3>
            <p className="text-gray-700">
              UPost is an AI-powered writing platform designed for authors and screenwriters. 
              Our platform helps writers create 10x faster with AI assistance, convert novels 
              to screenplays, and publish directly to Amazon KDP. Features include Multi-Revision AI, 
              real-time collaboration, copyright protection, and comprehensive publishing tools.
            </p>
            <div className="mt-4">
              <Link to="/register">
                <Button>Start Writing Free</Button>
              </Link>
            </div>
          </div>

          {/* Related Press Releases */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-xl font-semibold mb-4">More Press Releases</h3>
            <Link to="/press">
              <Button variant="outline">
                View All Press Releases
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </>
  );
}
